/**
 * Consultation-prep fetcher.
 * ─────────────────────────
 * Calls the real `/api/v2/Precision/Report/GetConsultation/{customerId}`
 * endpoint and runs the response through the shape adapters that smooth
 * over the discrepancies documented in
 * `claude_docs/CONSULTATION_BACKEND_FIXES_V1.md`. Returns a typed
 * `ConsultationPrep` object the consultation UI can render directly.
 *
 * The adapters are intentionally tolerant — when the backend ships
 * spec-compliant shapes they become no-ops, so future server-side cleanups
 * never break the frontend.
 *
 * On network / parse failure we return a safe `EMPTY_PREP` fallback rather
 * than `null` so consumers that hard-deref `data.prep!.x` don't crash and
 * the consultation can surface empty states instead of a deadlocked loader.
 */

import {
  MainMetricsTiles,
  BodyCompositionData,
  EnergyTrainingData,
} from "./consultationTypes";
import { BioAgeMeasurement, BioAgeProjectionBlock } from "./consultationTypes";
import { getConsultation } from "../../../common/network/Api";
import { isValidDate } from "../../../providers/helperFunctions";

export type ConsultationPrep = {
  bioAge: BioAgeMeasurement;
  /**
   * Deferred for v1 — not shipped by the current
   * `/api/v2/Precision/Report/GetConsultation` endpoint (discrepancy #2 in
   * CONSULTATION_BACKEND_BRIEF.md). Consumers (`BioAgeReductionCard`,
   * stepper target-age chip) are commented out. Make required again once
   * the backend ships `{ targetBioAge, weeksToTarget, totalImprovementYears }`.
   */
  bioAgeProjection?: BioAgeProjectionBlock;
  bioAgeHistory: BioAgeMeasurement;
  mainMetricsTiles: MainMetricsTiles;
  bodyComposition: BodyCompositionData;
  energyTraining: EnergyTrainingData;
};

// --- bioAge / bioAgeHistory shape normalizer --------------------------------
// Discrepancy #1 from CONSULTATION_BACKEND_FIXES_V1.md: backend ships
// PascalCase keys (`Biological`, `Chronological`, `ReportDate`) and a
// US-formatted date string (`"3/23/2025 12:48:30 PM"`). Tolerant of both
// shapes — if the backend later switches to camelCase + ISO, this stays a
// no-op.
const toIsoDate = (s: string | undefined | null): string => {
  if (!s) return s ?? "";
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  // Reuse the project-wide validity check (`src/providers/helperFunctions`)
  // instead of inlining `Number.isNaN(d.getTime())` — keeps the predicate
  // consistent with the rest of the codebase (e.g. `BiomarkerModal`).
  return isValidDate(d) ? d.toISOString().slice(0, 10) : s;
};

const normalizeBioAge = (raw: any): BioAgeMeasurement => {
  if (raw == null) {
    return { biological: 0, chronological: 0, reportDate: "" };
  }
  return {
    biological: raw.biological ?? raw.Biological ?? 0,
    chronological: raw.chronological ?? raw.Chronological ?? 0,
    reportDate: toIsoDate(raw.reportDate ?? raw.ReportDate),
  };
};

/**
 * True when a `BioAgeMeasurement` carries no real data.
 *
 * The backend doesn't consistently send `null` for "no history" — sometimes
 * it ships `{ Biological: 0, Chronological: 0, ReportDate: null }` instead.
 * After `normalizeBioAge` runs, that empty payload becomes
 * `{ biological: 0, chronological: 0, reportDate: "" }`, which is truthy
 * and slips past a plain `m != null` guard.
 *
 * Callers should treat "null OR empty" as "no history" — keep the existing
 * null check AND combine with this helper.
 */
const isEmptyBioAgeMeasurement = (
  m: BioAgeMeasurement | null | undefined
): boolean => {
  if (m == null) return true;
  return (
    (m.biological ?? 0) === 0 && (m.chronological ?? 0) === 0 && !m.reportDate
  );
};

// --- mainMetricsTiles shape normalizer --------------------------------------
// Discrepancies #3 + #4 from CONSULTATION_BACKEND_FIXES_V1.md:
//   #3 — Backend ships an ARRAY of single-key envelopes rather than one
//        object with five named keys. Flatten via reduce.
//   #4 — `fatcarb` tile uses lowercase `fatpct/carbpct`. Tolerant rename.
const normalizeFatcarb = (raw: any): any => {
  if (raw == null) return { fatPct: 0, carbPct: 0 };
  return {
    fatPct: raw.fatPct ?? raw.fatpct ?? 0,
    carbPct: raw.carbPct ?? raw.carbpct ?? 0,
  };
};

const normalizeMainMetricsTiles = (raw: any): any => {
  if (raw == null) return raw;
  const flat = Array.isArray(raw)
    ? raw.reduce(
        (acc, entry) => ({ ...acc, ...entry }),
        {} as Record<string, any>
      )
    : raw;
  if (flat.fatcarb) flat.fatcarb = normalizeFatcarb(flat.fatcarb);
  return flat;
};

// --- bodyComposition shape normalizer ---------------------------------------
// Discrepancy #5 was originally fixed client-side, but backend has since
// shipped the spec-compliant `leftArm | rightArm | trunk | leftLeg |
// rightLeg` keys. This adapter is now mostly a no-op (the prefix-match
// branch only runs for the legacy verbose-key shape).
const SEGMENT_PREFIX_MAP: Array<[string, string]> = [
  ["left arm", "leftArm"],
  ["right arm", "rightArm"],
  ["left leg", "leftLeg"],
  ["right leg", "rightLeg"],
  ["trunk", "trunk"],
];

const segmentIdForKey = (key: string): string | null => {
  if (["leftArm", "rightArm", "trunk", "leftLeg", "rightLeg"].includes(key)) {
    return key;
  }
  const lower = key.toLowerCase();
  for (const [prefix, id] of SEGMENT_PREFIX_MAP) {
    if (lower.startsWith(prefix)) return id;
  }
  return null;
};

const normalizeBodyComposition = (raw: any): BodyCompositionData => {
  if (!raw) return { deviceName: "", tabs: [] };
  return {
    deviceName: raw.deviceName ?? "",
    tabs: (raw.tabs ?? []).map((tab: any) => {
      const segs = tab?.segments ?? {};
      const rekeyed: Record<string, any> = {};
      Object.keys(segs).forEach((k) => {
        const id = segmentIdForKey(k);
        if (id) rekeyed[id] = segs[k];
      });
      return { id: tab.id, label: tab.label, segments: rekeyed };
    }),
  };
};

// Re-exported so other surfaces (e.g. the score screen's body comp swap)
// can apply the same adapters to a raw `GetConsultation` response.
export {
  normalizeBioAge,
  isEmptyBioAgeMeasurement,
  normalizeMainMetricsTiles,
  normalizeBodyComposition,
};

// --- The fetcher ------------------------------------------------------------

// Safe default returned when the network call fails. Components that
// hard-deref `data.prep!.x` don't crash; panels render their empty states.
const EMPTY_PREP: ConsultationPrep = {
  bioAge: { biological: 0, chronological: 0, reportDate: "" },
  bioAgeHistory: { biological: 0, chronological: 0, reportDate: "" },
  mainMetricsTiles: {
    muscle: { value: 0, unit: "", score: 0, scoreCategory: 0 },
    rmr: { value: 0, unit: "", score: 0, scoreCategory: 0 },
    vo2: { value: 0, unit: "", score: 0, scoreCategory: 0 },
    bodyfat: { value: 0, unit: "", score: 0, scoreCategory: 0 },
    fatcarb: { fatPct: 0, carbPct: 0 },
  } as MainMetricsTiles,
  bodyComposition: { deviceName: "", tabs: [] },
  energyTraining: { zones: [] },
};

/**
 * Fetches the consultation-prep payload from the real backend and
 * normalizes it through the shape adapters above. On error returns
 * `EMPTY_PREP` (so the UI doesn't deadlock on the loader).
 *
 * @param customerId — used for both the path param and as the "no fetch"
 *   guard. `null` (no client selected) returns `EMPTY_PREP` immediately.
 */
export const fetchConsultationPrep = async (
  customerId: string | null
): Promise<ConsultationPrep> => {
  if (!customerId) return EMPTY_PREP;
  try {
    const resp = await getConsultation(customerId);
    return {
      bioAge: normalizeBioAge(resp?.bioAge),
      bioAgeHistory: normalizeBioAge(resp?.bioAgeHistory),
      mainMetricsTiles: normalizeMainMetricsTiles(resp?.mainMetricsTiles),
      bodyComposition: normalizeBodyComposition(resp?.bodyComposition),
      energyTraining: resp?.energyTraining ?? { zones: [] },
      // bioAgeProjection intentionally omitted — backend doesn't ship it
      // yet (deferred for v1). The field is optional on `ConsultationPrep`.
    };
  } catch {
    // On failure return the empty placeholder; downstream panels render
    // their empty states rather than dead-locking on the loader.
    return EMPTY_PREP;
  }
};
